import { useEffect, useMemo } from "react";

import qs from "query-string";
import styled, { css } from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import { Box } from "@similarweb/ui-components";

import { useWebsiteContentTouchHookDummyPage } from "app/pages/website-analysis/website-structure/pages/website-content-locked/isWebsiteContentTouchHookDummyPage";
import { PreviewInteractionBlocker } from "app/pages/website-analysis/website-structure/pages/website-content-locked/PreviewInteractionBlocker";
import { translate } from "app/filters/i18nFilter";
import { CellDropdownSWReactTableOnRowHover } from "app/pages/website-analysis/pages/components/CellSiteDropdown";
import { WithSegmentsLoading } from "app/pages/industry-analysis/frame/WithSegmentsLoading";

import { TABLE_CONFIG } from "../../constants";
import type { IFoldersItem, IFoldersTransformedData } from "../../types";
import { FoldersTableTop } from "../FoldersTableTop/FoldersTableTop";

import { FoldersTableColumns } from "./columnConfig";
import { useFoldersConfig } from "./hooks/useFoldersConfig";
import { useFoldersData } from "./hooks/useFoldersData";
import { useTableExpansion } from "./hooks/useTableExpansion";
import { useTableSelection } from "./hooks/useTableSelection/useTableSelection";

const lockedDummyPageTableStyles = css`
    position: relative;
    margin-top: 205px;

    .ant-table-thead,
    .ant-table-header,
    .ant-table-body,
    .ant-table-content,
    .ant-table-tbody,
    .ant-table-tbody-virtual-holder,
    .ant-table-tbody-virtual-holder-inner,
    .ant-table-pagination {
        pointer-events: none;
        user-select: none;
    }

    .ant-table-tbody,
    .ant-table-pagination {
        filter: blur(6px);
    }
`;

const TableWrapper = styled(Box)<{ $isLockedDummyPage?: boolean }>`
    width: 100%;
    max-width: 1368px;
    height: auto;
    border-radius: 6px;
    overflow: hidden;
    display: block;
    ${CellDropdownSWReactTableOnRowHover}
    ${({ $isLockedDummyPage }) => ($isLockedDummyPage ? lockedDummyPageTableStyles : "")}

    div.ant-table-cell.ant-table-selection-column {
        justify-content: center;
    }

    .ant-table-tbody {
        .ant-table-row {
            div.ant-table-cell {
                display: flex;
                align-items: center;
            }

            .ant-table-cell-content {
                width: 100%;
            }
        }
    }
`;

const noop = () => undefined;

const FoldersTable = () => {
    const isLockedDummyPage = useWebsiteContentTouchHookDummyPage();
    const config = useFoldersConfig();
    const {
        currentDomain,
        setCurrentDomain,
        filters,
        sortConfig,
        radioFilters,
        handleSort,
        handleFilterChange,
        tablePagination: configTablePagination,
        searchTerm,
    } = config;

    const { transformedData, isTableLoading } = useFoldersData(filters, searchTerm) as {
        transformedData: IFoldersTransformedData;
        isTableLoading: boolean;
    };

    const { expandedRowKeys, onExpandedRowsChange, rowExpandable, onExpand } = useTableExpansion(
        transformedData.data,
        !!filters.filter,
        { isNew: filters.isNew, isTrending: filters.isTrending },
    ) as {
        expandedRowKeys: string[];
        onExpandedRowsChange: (keys: readonly React.Key[]) => void;
        rowExpandable: (record: IFoldersItem) => boolean;
        onExpand: (expanded: boolean, record: IFoldersItem) => void;
    };

    const { setSelectedRowKeys, tableSelection, selectedFoldersForSegment } = useTableSelection(transformedData.data);

    useEffect(() => {
        setSelectedRowKeys([]);
    }, [filters.isAll, filters.isNew, filters.isTrending, currentDomain, setSelectedRowKeys]);

    const tablePagination = {
        ...configTablePagination,
        total: transformedData.totalTopLevelCount,
    };

    const columnConfig = useMemo(() => {
        return FoldersTableColumns.getColumns(
            transformedData.totalCount,
            tablePagination.current,
            tablePagination.pageSize,
            sortConfig,
        );
    }, [transformedData.totalCount, tablePagination.current, tablePagination.pageSize, sortConfig]);
    const excelLink = `widgetApi/LeadingFolders/LeadingFolders/Excel?${qs.stringify(filters)}`;

    const foldersTableTop = (
        <FoldersTableTop
            excelLink={excelLink}
            initialFilters={filters}
            radioFilters={radioFilters}
            onFilterChange={handleFilterChange}
            downloadExcelPermitted={true}
            currentDomain={currentDomain}
            setCurrentDomain={setCurrentDomain}
            selectedFoldersForSegment={selectedFoldersForSegment}
            searchTerm={searchTerm}
        />
    );

    const tableContent = (
        <TableWrapper
            $isLockedDummyPage={isLockedDummyPage}
            data-automation="folders-table"
        >
            <Table
                key={`folders-table-${sortConfig.columnKey}-${sortConfig.order}-${tablePagination.current}`}
                data={transformedData.data}
                columnConfig={columnConfig}
                isLoading={isLockedDummyPage ? false : isTableLoading}
                pagination={tablePagination}
                loaderRowCount={TABLE_CONFIG.LOADER_ROW_COUNT}
                noData={{
                    subtitle: translate("global.nodata.notavilable"),
                }}
                onSort={isLockedDummyPage ? noop : handleSort}
                expandable={{
                    expandIconColumnIndex: TABLE_CONFIG.EXPAND_ICON_COLUMN_INDEX,
                    onExpand: isLockedDummyPage ? noop : onExpand,
                    expandedRowKeys,
                    onExpandedRowsChange: isLockedDummyPage ? noop : onExpandedRowsChange,
                    rowExpandable,
                }}
                virtual={true}
                scroll={{ y: 1000 }}
                selection={isLockedDummyPage ? undefined : tableSelection}
            >
                {foldersTableTop}
            </Table>
        </TableWrapper>
    );

    if (isLockedDummyPage) {
        return (
            <PreviewInteractionBlocker data-automation="folders-table-locked-interaction-blocker">
                {tableContent}
            </PreviewInteractionBlocker>
        );
    }

    return tableContent;
};

const FoldersTableWithSegmentsLoading = WithSegmentsLoading(FoldersTable);
export default FoldersTableWithSegmentsLoading;
